import React, { Suspense } from 'react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { ArrowRight, ArrowUpRight } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import SEOMetadata from '@/components/SEOMetadata';
import Loader from '@/Loader';
import { useIdlePrefetch } from '@/hooks/useIdlePrefetch';

const loadGetStarted = () => import('@/components/landing/GetStarted');
const GetStarted = React.lazy(loadGetStarted);
const loadFooter = () => import('@/components/landing/Footer');
const Footer = React.lazy(loadFooter);

import { getAllPosts } from '@/lib/blog';
import { breakpoints } from '@/layouts/breakpoints';

/* ─── Wrapper ─── */

const Wrapper = styled.div`
  min-height: 100vh;
  background: #fff;
  padding-top: 40px;

  @media (max-width: ${breakpoints.small}) {
    padding-top: 80px;
  }
`;

const Container = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  padding: 72px 40px;

  @media (max-width: ${breakpoints.medium}) {
  padding: 50px 20px;
}
`;

/* ─── Hero (featured post) ─── */

const HeroSection = styled(Link)`
  display: grid;
  grid-template-columns: 1fr 1.1fr;
  gap: 56px;
  align-items: center;
  padding-bottom: 64px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  margin-bottom: 56px;

  @media (max-width: ${breakpoints.medium}) {
    grid-template-columns: 1fr;
    gap: 32px;
  }

  @media (max-width: ${breakpoints.small}) {
    padding-bottom: 40px;
    gap: 24px;
  }
`;

const HeroLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const HeroMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
`;

const LatestBadge = styled.span`
  display: flex;
  align-items: center;
  gap: 7px;
  font-size: 13px;
  font-weight: 500;
  color: #111;

  &::before {
    content: '';
    display: block;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #22c55e;
    flex-shrink: 0;
  }
`;

const HeroDate = styled.span`
  font-size: 13px;
  color: #666;
`;

const HeroTitle = styled.h1`
  letter-spacing: -0.025em;
  line-height: 1.08;
  color: #0a0a0a;
  margin: 0;
  font-size: 64px!important;

  @media (max-width: ${breakpoints.large}) {
    font-size: 48px!important;
  }

  @media (max-width: ${breakpoints.small}) {
    font-size: 32px!important;
  }

`;

const HeroDesc = styled.p`
  font-size: 15px;
  color: #666;
  line-height: 1.65;
  margin: 0;
  max-width: 380px;

  @media (max-width: ${breakpoints.small}) {
    max-width: none;
    font-size: 14px;
  }
`;

const ReadMoreButton = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 9px 18px;
  border: 1px solid rgba(0, 0, 0, 0.18);
  border-radius: 6px;
  font-size: 13.5px;
  font-weight: 500;
  color: #111;
  text-decoration: none;
  width: fit-content;
  transition: background 0.15s, border-color 0.15s;

  &:hover {
    background: #f5f5f5;
    border-color: rgba(0, 0, 0, 0.28);
  }
`;

const HeroImage = styled.div`
  aspect-ratio: 16 / 10;
  border-radius: 6px;
  overflow: hidden;
  background: #f0f0f0;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.4s ease;
  }

  &:hover img {
  }
`;

const HeroImagePlaceholder = styled.div`
  aspect-ratio: 16 / 10;
  border-radius: 6px;
  background: linear-gradient(135deg, #e8e8e8 0%, #d0d0d0 100%);
`;

const RecentCardPlaceholder = styled.div`
  aspect-ratio: 16 / 10;
  border-radius: 10px;
  background: #f0f0f0;

  @media (max-width: ${breakpoints.small}) {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    border-radius: 0;
    aspect-ratio: auto;
  }
`;

/* ─── Recent posts (4-up grid) ─── */

const RecentGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 28px;
  padding-bottom: 64px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  margin-bottom: 56px;

  @media (max-width: ${breakpoints.large}) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: ${breakpoints.medium}) {
    grid-template-columns: 1fr 1fr;
    gap: 20px;
  }

  @media (max-width: ${breakpoints.small}) {
    grid-template-columns: 1fr;
    gap: 24px;
    padding-bottom: 48px;
  }

  @media (max-width: ${breakpoints.xsmall}) {
    gap: 20px;
    padding-bottom: 40px;
  }
`;

const RecentCard = styled(Link)`
  display: flex;
  flex-direction: column;
  gap: 11px;
  text-decoration: none;
  color: inherit;

  @media (max-width: ${breakpoints.small}) {
    position: relative;
    min-height: 240px;
    height: 240px;
    border-radius: 10px;
    overflow: hidden;
    gap: 0;
  }
`;

const RecentCardImage = styled.div`
  aspect-ratio: 16 / 10;
  border-radius: 6px;
  overflow: hidden;
  background: #f0f0f0;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.35s ease;
  }

  ${RecentCard}:hover & img {
  }

  @media (max-width: ${breakpoints.small}) {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    border-radius: 0;
    aspect-ratio: auto;

    &::after {
      content: '';
      position: absolute;
      inset: 0;
      background: linear-gradient(to top, rgba(0,0,0,0.8) 60%, rgba(0,0,0,0.1) 100%);
      z-index: 1;
    }
  }
`;

const RecentCardBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;

  @media (max-width: ${breakpoints.small}) {
    position: relative;
    z-index: 2;
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    gap: 12px;
    padding: 24px;
    height: 100%;
  }
`;

const RecentCardDate = styled.span`
  font-size: 12px;
  color: #666;
  letter-spacing: 0.01em;

  @media (max-width: ${breakpoints.small}) {
    color: rgba(255, 255, 255, 0.65);
  }
`;

const RecentCardTitle = styled.h2`
  font-weight: 600;
  font-size: 1.17em;
  color: #111;
  line-height: 1.45;
  margin: 0;
  transition: color 0.15s;

  ${RecentCard}:hover & {
    color: #2563eb;
  }

  @media (max-width: ${breakpoints.small}) {
    font-size: 15px;
    color: #fff;
  }
`;

const RecentCardDesc = styled.p`
  font-size: 13px;
  color: #555;
  line-height: 1.55;
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;

  @media (max-width: ${breakpoints.small}) {
    color: rgba(255, 255, 255, 0.8);
    -webkit-line-clamp: 3;
  }
`;

const RecentCardCTA = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12.5px;
  font-weight: 500;
  color: #999;
  transition: color 0.15s;

  ${RecentCard}:hover & {
    color: #2563eb;
  }

  svg {
    width: 13px;
    height: 13px;
    transition: transform 0.15s;
  }

  ${RecentCard}:hover & svg {
    transform: translate(2px, -1px);
  }

  @media (max-width: ${breakpoints.small}) {
    color: #fff;

    ${RecentCard}:hover & {
      color: rgba(255, 255, 255, 0.7);
    }
  }
`;

/* ─── All posts list ─── */

const AllPostsTitle = styled.h2`
  font-size: 18px;
  font-weight: 700;
  color: #111;
  margin: 0 0 4px;
  padding-bottom: 12px;
  letter-spacing: -0.01em;
`;

const PostList = styled.div`
  display: flex;
  flex-direction: column;
`;

const PostListItem = styled(Link)`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 32px;
  padding: 18px 0;
  border-top: 1px solid rgba(0, 0, 0, 0.07);
  text-decoration: none;
  color: inherit;
  transition: opacity 0.15s;
  >div{
    width: 100%;
  }

  &:last-child {
    border-bottom: 1px solid rgba(0, 0, 0, 0.07);
  }

  &:hover {
    opacity: 0.55;
  }

  @media (max-width: ${breakpoints.medium}) {
    flex-direction: column;
    gap: 4px;
  }

  @media (max-width: ${breakpoints.small}) {
    padding: 14px 0;
  }

  @media (max-width: ${breakpoints.xsmall}) {
    padding: 12px 0;
  }
`;

const PostListLeft = styled.div`
  flex: 1;
  min-width: 0;
`;

const PostListTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #111;
  margin: 0 0 3px;
  line-height: 1.4;
`;

const PostListDesc = styled.p`
  font-size: 11px;
  color: #555;
  line-height: 1.55;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 640px;
`;

const PostListDate = styled.span`
  font-size: 13px;
  color: #777;
  white-space: nowrap;
  flex-shrink: 0;

  @media (max-width: ${breakpoints.medium}) {
    font-size: 12px;
    color: #888;
  }
`;

/* ─── Utility ─── */

const LoadingState = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 100px 0;
  color: #ccc;
  font-size: 17px;
`;

/* ─── Component ─── */

export default function BlogPage() {
  useIdlePrefetch([loadGetStarted, loadFooter]);
  const [posts, setPosts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getAllPosts().then(posts => {
      setPosts(posts);
      setLoading(false);
    });
  }, []);


  if (loading) {
    return (
      <LoadingState>
        <Loader> </Loader>
      </LoadingState>
    );
  }

  const [featured, ...rest] = posts;
  const recent = rest.slice(0, 4);
  const older = rest.slice(4);

  return (
    <>
      <SEOMetadata
        title="Blog – Financial Data API Insights | AxionQuant"
        description="Insights, tutorials, and news from the AxionQuant financial data API team. Learn about market data, alternative data, and quantitative finance."
        canonicalUrl="https://axionquant.com/blog"
      />

      <Helmet>
        {featured?.image && (
          <link rel="preload" href={featured.image} as="image" />
        )}
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "BreadcrumbList",
              "itemListElement": [
                { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://axionquant.com/" },
                { "@type": "ListItem", "position": 2, "name": "Blog", "item": "https://axionquant.com/blog/" }
              ]
            },
            {
              "@type": "CollectionPage",
              "name": "AxionQuant Blog",
              "description": "Exploring financial data, quantitative research, alternative data, and market analysis.",
              "url": "https://axionquant.com/blog/"
            }
          ]
        })}</script>
      </Helmet>

      <Wrapper>
        <Container>

          {/* ── Featured post ── */}
          {featured && (
            <HeroSection  to={`/blog/${featured.slug}`}>
              <HeroLeft>
                <HeroMeta>
                  <LatestBadge>Latest post</LatestBadge>
                  <HeroDate>{(featured.date)}</HeroDate>
                </HeroMeta>

                <HeroTitle className="serif">{featured.title}</HeroTitle>

                {featured.description && (
                  <HeroDesc>{featured.description}</HeroDesc>
                )}

                <ReadMoreButton>
                  Read More <ArrowRight size={13} />
                </ReadMoreButton>
              </HeroLeft>

              {featured.image ? (
                <HeroImage>
                  <img src={featured.image} alt={featured.title} fetchPriority="high" width="528" height="330" />
                </HeroImage>
              ) : (
                <HeroImagePlaceholder />
              )}
            </HeroSection>
          )}

          {/* ── Recent 4 posts ── */}
          {recent.length > 0 && (
            <RecentGrid>
              {recent.map(post => (
                <RecentCard key={post.slug} to={`/blog/${post.slug}`}>
                  {post.image ? (
                    <RecentCardImage>
                      <img src={post.image} alt={post.title} loading="lazy" width="352" height="220" />
                    </RecentCardImage>
                  ) : (
                    <RecentCardPlaceholder />
                  )}
                  <RecentCardBody>
                    <RecentCardDate>{(post.date)}</RecentCardDate>
                    <RecentCardTitle className="serif">{post.title}</RecentCardTitle>
                    {post.description && (
                      <RecentCardDesc>{post.description}</RecentCardDesc>
                    )}
                    <RecentCardCTA>Read <ArrowUpRight /></RecentCardCTA>
                  </RecentCardBody>
                </RecentCard>
              ))}
            </RecentGrid>
          )}

          {/* ── All posts list ── */}
          {older.length > 0 && (
            <div>
              <AllPostsTitle>All posts</AllPostsTitle>
              <PostList>
                {older.map(post => (
                  <PostListItem key={post.slug} to={`/blog/${post.slug}`}>
                    <PostListLeft>
                      <PostListTitle className="serif">{post.title}</PostListTitle>
                      {post.description && (
                        <PostListDesc>{post.description}</PostListDesc>
                      )}
                    </PostListLeft>
                    <PostListDate>{(post.date)}</PostListDate>
                  </PostListItem>
                ))}
              </PostList>
            </div>
          )}

          {posts.length === 0 && (
            <EmptyState>No posts yet. Check back soon.</EmptyState>
          )}

        </Container>
      </Wrapper>
      <Suspense fallback={null}>
      <GetStarted/>
      <Footer/>
      </Suspense>
    </>
  );
}
